"use client";

import { ShieldCheck, Users, Zap } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BuyerJourney() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    icon: ShieldCheck,
    eyebrow: "Step 01",
    title: "Register your interest",
    body: "Create your account and define your specific procurement requirements.",
    badge: " 15+ years in electronics trading"
  }, {
    icon: Users,
    eyebrow: "Step 02",
    title: "Connect with suppliers",
    body: "Our platform matches your needs with verified global trade partners.",
    badge: " Verified Supplier Network"
  }, {
    icon: Zap,
    eyebrow: "Step 03",
    title: "Secure your trade",
    body: "Finalize terms and manage logistics through our streamlined dashboard.",
    badge: " 24-48 hr Quote SLA"
  }];
  return <section data-section-id="16352" id="buyerjourneyprocess" className="bg-white text-[#1a1a1a] py-28 md:py-40 font-sans">
      <div className="mx-auto max-w-[1240px] px-5 md:px-20">
        <div ref={intro.ref} className={`mx-auto max-w-[720px] text-center transition-all duration-[520ms] ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3.5"}`}>
          <h2 className="font-sans text-[#1a3a52] tracking-[0.75px] leading-[1.08]" style={{
          fontSize: "clamp(2.125rem, 4.2vw, 3.5rem)",
          fontWeight: 600
        }}>
            Global trade, simplified in <em className="italic font-sans text-[#d4a574] not-italic">three</em> steps.
          </h2>
          <p className="mt-6 text-[#1a1a1a]/80 leading-relaxed" style={{
          fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
          fontWeight: 400
        }}>
            From initial registration to final delivery, our platform ensures a transparent, efficient, and secure procurement experience for modern businesses.
          </p>
        </div>

        <div ref={grid.ref} className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-7">
          {steps.map((step, i) => {
          const Icon = step.icon;
          const delay = i * 110;
          return <div key={i} data-index={i} className="transition-all ease-out" style={{
            transitionDuration: "520ms",
            transitionDelay: `${delay}ms`,
            opacity: grid.isVisible ? 1 : 0,
            transform: grid.isVisible ? "translateY(0) scale(1)" : "translateY(14px) scale(0.985)"
          }}>
                <Card className="group bg-white border border-[#e0e0e0] p-9 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-[280ms] ease-out h-full rounded-lg">
                  <CardContent className="p-0 flex flex-col gap-5">
                    <div className="relative w-14 h-14 flex items-center justify-center">
                      <span className="absolute inset-0 rounded-lg bg-[#1a3a52]/10" aria-hidden="true" />
                      <Icon className="relative size-7 text-[#1a3a52]" strokeWidth={1.75} />
                    </div>

                    <div className="text-[#d4a574] uppercase" style={{
                  fontSize: "0.8125rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em"
                }}>
                      {step.eyebrow}
                    </div>

                    <div className="flex flex-col gap-3">
                      <h3 className="font-sans text-[#1a3a52] leading-[1.25] tracking-[0.75px]" style={{
                    fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)",
                    fontWeight: 600
                  }}>
                        {step.title}
                      </h3>
                      <span className="inline-flex items-center text-[11px] font-bold uppercase tracking-wider text-[#1a3a52] bg-[#1a3a52]/5 px-2.5 py-1 rounded-lg w-fit">
                        {step.badge}
                      </span>
                    </div>

                    <p className="text-[#1a1a1a]/70 leading-[1.6]" style={{
                  fontSize: "1rem",
                  fontWeight: 400
                }}>
                      {step.body}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}